/**
 * @component BasicDetails
 * @description A comprehensive form component for collecting and managing basic event details.
 * This includes event name, short name, category, subcategory, format, visibility, and mode of engagement.
 *
 * @process
 * 1. Retrieves event setup data from Redux store
 * 2. Initializes form state and handlers via the useBasicDetails custom hook
 * 3. Renders a form with multiple input fields for event details
 * 4. Provides edit mode functionality for published events
 * 5. Handles form validation via Formik
 * 6. Manages character limits for specific fields
 * 7. Provides autocomplete functionality for categories, subcategories, and formats
 * 8. Allows toggle between public and private event visibility
 * 9. Provides radio button selection for online vs in-person events
 *
 * @returns {JSX.Element} A form for collecting and managing basic event details with validation
 * and responsive UI components from Material-UI
 */

import React from 'react';
import { useSelector } from 'react-redux';
import {
    Button,
    TextField,
    Autocomplete,
    FormHelperText,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress
} from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { PrivateIcon, PublicIcon } from '../../../../../../../../icons/FontAwesomeIcon.js';
import useBasicDetails from '../../../hooks/useBasicDetails.js';
import RadioButtons from '../../../../../../../../components/FormFields/Radio.jsx';
import ShowDoneIconOnSelect from '../../../../../../../../icons/ShowDoneIconOnSelect.jsx';
import { H6, Body3 } from '../../../../../../../../themes/typography.js';
import '../../../../../../../../styles/components/BasicDetails.css';

export default function BasicDetails() {
    const { categories, formats } = useSelector((state) => state.eventSetup);

    const {
        formik,
        loading,
        isEditModeOn,
        characterLimit,
        countCharacter,
        shouldShowEditButton,
        filteredSubcategories,
        setIsEditModeOn,
        handleShortNameChange,
        handleModeOfEventSelection,
        handleFormatFilterOptions,
        handleFormatOptionChange,
        getFormatOptionLabel,
        handleCategoryFilterOptions,
        handleCategoryOptionChange,
        getCategoryOptionLabel,
        handleSubCategoryFilterOptions,
        getSubCategoryOptionLabel,
        handleSubCategoryOptionChange
    } = useBasicDetails();

    return (
        <React.Fragment>
            <form className="event-basic-details-form" onSubmit={formik.handleSubmit} noValidate>
                <div className="basic-detail-event-form">
                    {shouldShowEditButton && (
                        <div style={editBtnStyle}>
                            <Button
                                startIcon={<BorderColorIcon />}
                                variant="text"
                                onClick={() => setIsEditModeOn(true)}
                                disabled={isEditModeOn}
                            >
                                Edit
                            </Button>
                        </div>
                    )}

                    <div className="basic-detail-form-starts" style={{ paddingTop: '0.5rem' }}>
                        {/* Event name */}
                        <div className="event-basic-details-input-container">
                            <div className="basic-input-container">
                                <TextField
                                    label="Event Name"
                                    fullWidth={true}
                                    required={true}
                                    type="text"
                                    id="eventName"
                                    name="eventName"
                                    disabled={!isEditModeOn}
                                    placeholder="Enter event name"
                                    value={formik.values.eventName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.errors.eventName && formik.touched.eventName
                                            ? true
                                            : false
                                    }
                                    helperText={formik.touched.eventName && formik.errors.eventName}
                                />
                            </div>
                        </div>
                        <div className="event-basic-details-input-container">
                            {/* Short name */}
                            <div className="format-input-container">
                                <TextField
                                    label="Event Short Name"
                                    fullWidth={true}
                                    required={true}
                                    type="text"
                                    id="eventDisplayName"
                                    name="eventDisplayName"
                                    placeholder="Enter event short name"
                                    disabled={!isEditModeOn}
                                    value={formik.values.eventShortName}
                                    onChange={handleShortNameChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.eventShortName &&
                                        Boolean(formik.errors.eventShortName)
                                    }
                                    helperText={
                                        formik.touched.eventShortName &&
                                            formik.errors.eventShortName
                                            ? formik.errors.eventShortName
                                            : 'A short event name shown to attendees in our card listings.'
                                    }
                                />
                                <div className="counter-limit">
                                    <FormHelperText>
                                        {`${countCharacter} / ${characterLimit}`}
                                    </FormHelperText>
                                </div>
                            </div>

                            {/* Categories */}
                            <div className="format-input-container">
                                <Autocomplete
                                    freeSolo
                                    clearOnBlur
                                    selectOnFocus
                                    fullWidth
                                    multiple={false}
                                    id="eventCategory"
                                    options={categories || []}
                                    slotProps={slotProps}
                                    disabled={!isEditModeOn}
                                    getOptionLabel={getCategoryOptionLabel}
                                    onChange={handleCategoryOptionChange}
                                    filterOptions={handleCategoryFilterOptions}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value?.id
                                    }
                                    value={
                                        categories.find((e) => e.id === formik.values.category) ||
                                        formik.values.category
                                    }
                                    onBlur={formik.handleBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            name="eventCategory"
                                            label="Event Category"
                                            variant="outlined"
                                            placeholder="Select or add your custom category"
                                            error={
                                                formik.touched.category &&
                                                Boolean(formik.errors.category)
                                            }
                                            helperText={
                                                formik.touched.category && formik.errors.category
                                                    ? formik.errors.category
                                                    : 'Select the Category your event best falls under.'
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="event-basic-details-input-container">
                            {/* Event Visibility */}
                            <div className="event-visibility-container">
                                <H6>Event Visibility *</H6>
                                <div className="event-visibility-selection">
                                    <ToggleButtonGroup
                                        color="main"
                                        disabled={!isEditModeOn}
                                        value={formik.values.isEventPrivate}
                                        exclusive
                                        onChange={(e, value) =>
                                            formik.setFieldValue('isEventPrivate', value)
                                        }
                                        aria-label="Event Visibility"
                                    >
                                        <ToggleButton value={false}>
                                            <PublicIcon /> Public
                                            {formik.values.isEventPrivate === false && (
                                                <ShowDoneIconOnSelect />
                                            )}
                                        </ToggleButton>
                                        <ToggleButton value={true}>
                                            <PrivateIcon /> Private
                                            {formik.values.isEventPrivate === true && (
                                                <ShowDoneIconOnSelect />
                                            )}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </div>
                                <FormHelperText
                                    error={
                                        formik.errors.isEventPrivate &&
                                        formik.touched.isEventPrivate
                                    }
                                >
                                    {formik.errors.isEventPrivate && formik.touched.isEventPrivate
                                        ? formik.errors.isEventPrivate
                                        : null}
                                </FormHelperText>
                            </div>

                            {/* Sub category */}
                            <div className="format-input-container">
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    freeSolo
                                    id="eventSubCategory"
                                    options={filteredSubcategories}
                                    value={formik.values.subCategory}
                                    slotProps={slotProps}
                                    disableCloseOnSelect
                                    onChange={handleSubCategoryOptionChange}
                                    filterOptions={handleSubCategoryFilterOptions}
                                    renderOption={(props, option) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={key} {...optionProps}>
                                                {option.name}
                                            </li>
                                        );
                                    }}
                                    getOptionLabel={getSubCategoryOptionLabel}
                                    onBlur={formik.handleBlur}
                                    disabled={(!formik.values?.category?.id && !formik.values?.category) || !isEditModeOn}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            name="eventSubCategory"
                                            label="Event Sub Category"
                                            variant="outlined"
                                            placeholder={
                                                formik.values.subCategory.length === 0
                                                    ? 'Select or add your custom sub-categories'
                                                    : ''
                                            }
                                            error={
                                                formik.touched.subCategory &&
                                                Boolean(formik.errors.subCategory)
                                            }
                                            helperText={
                                                formik.errors.subCategory &&
                                                    formik.touched.subCategory
                                                    ? formik.errors.subCategory
                                                    : 'Select Up to 5 Sub-Category types your event best falls under based on the Main Category selected above.'
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="event-basic-details-input-container">
                            {/* Format */}
                            <div className="format-input-container">
                                <Autocomplete
                                    id="eventFormat"
                                    freeSolo
                                    fullWidth
                                    multiple
                                    clearOnBlur
                                    selectOnFocus
                                    disableCloseOnSelect
                                    options={formats || []}
                                    value={formik.values.format}
                                    slotProps={slotProps}
                                    disabled={!isEditModeOn}
                                    getOptionLabel={getFormatOptionLabel}
                                    onChange={handleFormatOptionChange}
                                    filterOptions={handleFormatFilterOptions}
                                    onBlur={formik.handleBlur}
                                    renderOption={(props, option) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={key} {...optionProps}>
                                                {option.name}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            name="eventFormat"
                                            label="Event Format"
                                            placeholder={
                                                formik.values.format.length === 0
                                                    ? 'Select or add your custom formats'
                                                    : ''
                                            }
                                            error={
                                                formik.touched.format &&
                                                Boolean(formik.errors.format)
                                            }
                                            helperText={
                                                formik.errors.format && formik.touched.format
                                                    ? formik.errors.format
                                                    : 'Select up to 3 event formats relevant to your event'
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="event-schedule-type-selection">
                            <div className="schedule-selection-container">
                                <div className="schedule-selection-title">
                                    <H6>Mode of engagement *</H6>
                                </div>

                                {/* Options of mode of engagement */}
                                <div className="schedule-selection-options">
                                    <div className="radio-input-container">
                                        <div className="radio-input">
                                            <RadioButtons
                                                checked={formik.values.isPhysicalEvent === false}
                                                onChange={handleModeOfEventSelection}
                                                value="false"
                                                name="modeOfEnagagement"
                                                disabled={!isEditModeOn}
                                            />
                                        </div>
                                        <div className="radio-input-label-and-text">
                                            <H6>Online Event</H6>
                                            <div className="radio-selection-caption">
                                                <Body3>
                                                    Host Online Event Using Zoom, Google Meet,
                                                    Youtube Live etc.
                                                </Body3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="radio-input-container">
                                        <div className="radio-input">
                                            <RadioButtons
                                                checked={formik.values.isPhysicalEvent === true}
                                                onChange={handleModeOfEventSelection}
                                                value="true"
                                                name="modeOfEnagagement"
                                                disabled={!isEditModeOn}
                                            />
                                        </div>
                                        <div className="radio-input-label-and-text">
                                            <H6>In-Person event</H6>
                                            <div className="radio-selection-caption">
                                                <Body3>
                                                    Host In-Person Events, Outings and Activities
                                                    using shmooz.
                                                </Body3>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <FormHelperText
                                    error={
                                        formik.errors.isPhysicalEvent &&
                                        formik.touched.isPhysicalEvent
                                    }
                                >
                                    {formik.errors.isPhysicalEvent && formik.touched.isPhysicalEvent
                                        ? formik.errors.isPhysicalEvent
                                        : null}
                                </FormHelperText>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="submit-button-container flex-end">
                    <Button variant="main" disabled={loading} type="submit">
                        {/* {isEditModeOn ? 'Save' : 'Next'} */}
                        Next{' '}
                        {loading && (
                            <CircularProgress
                                size={20}
                                sx={{ marginLeft: '0.75rem', color: 'var(--primary-color)' }}
                            />
                        )}
                    </Button>
                </div>
            </form>
        </React.Fragment>
    );
}

const slotProps = {
    popper: {
        placement: 'bottom',
        disablePortal: true,
        modifiers: [
            {
                name: 'flip',
                enabled: false
            }
        ]
    },
    chip: {
        variant: 'deletable'
    }
};

const editBtnStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    paddingBottom: '0.5rem'
};
